export interface WaitForElementOptions<T extends HTMLElement = HTMLElement> {
  selector: string // CSS selector for the element
  rootDocument?: Document | Element // optional, defaults to window.document
  timeoutMs?: number // max wait time in ms
  intervalMs?: number // check interval in ms
  onFound?: (el: T) => void // optional callback when element is found
  onNotFound?: () => void // optional callback when element is found
}

export function waitForElement<T extends HTMLElement = HTMLElement>(
  options: WaitForElementOptions<T>
): Promise<T | null> {
  const {
    selector,
    rootDocument = document,
    timeoutMs = 3000,
    intervalMs = 100,
    onFound,
    onNotFound,
  } = options

  return new Promise((resolve) => {
    const startTime = Date.now()
    let timer: number | null = null

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }

    const check = () => {
      const el = rootDocument.querySelector(selector) as T | null

      if (el) {
        stop()
        if (onFound) onFound(el)
        resolve(el)
        return
      }

      if (Date.now() - startTime >= timeoutMs) {
        stop()
        onNotFound?.()
        resolve(null) // not found
      }
    }

    timer = window.setInterval(check, intervalMs)
    check() // check immediately
  })
}
